import { useRef, useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from '@repo/ui/src/components/ui/sheet';
import { Button } from '@repo/ui/src/components/ui/button';
import { Label } from '@repo/ui/src/components/ui/label';
import { Textarea } from '@repo/ui/src/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui/src/components/ui/select';
import { useAssignPlan } from '../../hooks/useAdminOrgs';

const PLAN_OPTIONS = ['FREE', 'PLUS', 'PRO', 'CENTER'] as const;
const STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Đang hoạt động' },
  { value: 'TRIALING', label: 'Dùng thử' },
] as const;
const CYCLE_OPTIONS = [
  { value: 'MONTHLY', label: 'Theo tháng' },
  { value: 'ANNUAL', label: 'Theo năm' },
] as const;

interface AssignPlanSheetProps {
  orgId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The org's standing right now — the form opens pre-filled with it, so a
   *  re-grant (e.g. to fix a mis-set cycle) starts from reality instead of a
   *  hardcoded PRO/monthly. Only the expiry is recomputed, from the prefilled
   *  cycle. */
  current?: {
    planCode: string;
    subStatus: string | null;
    billingCycle: 'MONTHLY' | 'ANNUAL' | null;
  };
}

function isFutureDate(dateValue: string): boolean {
  if (!dateValue) return false;
  return new Date(dateValue).getTime() > Date.now();
}

/** yyyy-mm-dd in the viewer's local calendar — matches the `<input type="date">` format. */
function fmtDateInput(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Today + one cycle length — the date a fresh grant of that cycle would expire. */
function suggestUntil(cycle: (typeof CYCLE_OPTIONS)[number]['value']): string {
  const d = new Date();
  if (cycle === 'ANNUAL') d.setFullYear(d.getFullYear() + 1);
  else d.setMonth(d.getMonth() + 1);
  return fmtDateInput(d);
}

export function AssignPlanSheet({ orgId, open, onOpenChange, current }: AssignPlanSheetProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Gán gói</SheetTitle>
          <SheetDescription>
            Gán tay = gói tặng: hệ thống sẽ không tự động xuất hoá đơn gia hạn. Hết hạn, tổ chức tự
            chuyển về gói Free và vẫn dùng được, không bị khoá.
          </SheetDescription>
        </SheetHeader>
        <AssignPlanForm
          key={open ? orgId : 'closed'}
          orgId={orgId}
          onOpenChange={onOpenChange}
          current={current}
        />
      </SheetContent>
    </Sheet>
  );
}

function AssignPlanForm({
  orgId,
  onOpenChange,
  current,
}: {
  orgId: string;
  onOpenChange: (open: boolean) => void;
  current?: AssignPlanSheetProps['current'];
}) {
  // Open on the org's actual standing (Johnny 2026-08-29): a grant is usually a
  // correction or extension of what's already there, so PRO/monthly defaults
  // only apply when the org's plan isn't grantable (NONE). The expiry is the
  // one field NOT copied from the org — it's re-suggested from the prefilled
  // cycle, because the point of a re-grant is a fresh term.
  const initialPlan = (PLAN_OPTIONS as readonly string[]).includes(current?.planCode ?? '')
    ? (current!.planCode as (typeof PLAN_OPTIONS)[number])
    : 'PRO';
  const initialStatus = current?.subStatus === 'TRIALING' ? 'TRIALING' : 'ACTIVE';
  const initialCycle = current?.billingCycle ?? 'MONTHLY';
  // The org's plan when the sheet opened — captured once, same as
  // `initialPlan` above, so picking a plan and then picking it back doesn't
  // stop reading as "no change" (current stays what it was on open, not what
  // planCode currently is).
  const fromPlanCode = (PLAN_OPTIONS as readonly string[]).includes(current?.planCode ?? '')
    ? (current!.planCode as (typeof PLAN_OPTIONS)[number])
    : null;
  const [planCode, setPlanCode] = useState<(typeof PLAN_OPTIONS)[number]>(initialPlan);
  const [status, setStatus] = useState<(typeof STATUS_OPTIONS)[number]['value']>(initialStatus);
  const [billingCycle, setBillingCycle] = useState<(typeof CYCLE_OPTIONS)[number]['value']>(initialCycle);
  const [dateValue, setDateValue] = useState(initialPlan === 'FREE' ? '' : suggestUntil(initialCycle));
  const [note, setNote] = useState('');
  // True once the teacher has typed into the date field by hand — after that,
  // picking a different cycle must not clobber their date. Clearing the field
  // back to empty makes it eligible for suggestion again regardless.
  const dateHandEdited = useRef(false);

  const assignPlan = useAssignPlan(orgId);
  const isPending = assignPlan.isPending;
  // FREE is the floor and takes no expiry; every paid plan needs a future one.
  const isFree = planCode === 'FREE';
  const canSubmit = !isPending && (isFree || isFutureDate(dateValue));
  // Ruling 10: a mid-month admin downgrade suspends BankHub auto-reconcile
  // immediately — a real behaviour change, not just a plan label change, so
  // it earns a line here rather than silently showing up as "reconcile
  // stopped working" later. PLAN_OPTIONS' own order IS the reconcile-quota
  // order today (FREE 0 < Plus 60 < Pro 150 < Center 500), so comparing
  // index avoids duplicating the quota numbers themselves here — this file
  // has no access to seedPlans.ts's numbers.
  const isDowngrade =
    fromPlanCode !== null && PLAN_OPTIONS.indexOf(planCode) < PLAN_OPTIONS.indexOf(fromPlanCode);
  // The "self-heals next month" framing is true ONLY for a paid→paid
  // downgrade: sepayCompany.ts's `desiredCompanyActive` reads a monthly
  // usage count against the NEW plan's quota, which resets every month by
  // construction (countReconcileEventsThisMonth). But quota 0 (Free) is not
  // a cap that resets — `desiredCompanyActive` returns false unconditionally
  // for it, forever, until the org is on a paid plan again. So "nếu vượt hạn
  // mức" and "mở lại đầu tháng sau" are both wrong for a →Free downgrade,
  // the single most common one this sheet performs.
  const isFreeDowngrade = isDowngrade && isFree;

  function handleCycleChange(cycle: (typeof CYCLE_OPTIONS)[number]['value']) {
    setBillingCycle(cycle);
    if (dateValue === '' || !dateHandEdited.current) {
      setDateValue(suggestUntil(cycle));
    }
  }

  function handlePlanChange(code: (typeof PLAN_OPTIONS)[number]) {
    setPlanCode(code);
    // A FREE-prefilled form has no date; flipping to a paid plan reveals the
    // field, so seed it the same way a cycle pick would.
    if (code !== 'FREE' && (dateValue === '' || !dateHandEdited.current)) {
      setDateValue(suggestUntil(billingCycle));
    }
  }

  function handleDateChange(e: React.ChangeEvent<HTMLInputElement>) {
    setDateValue(e.target.value);
    dateHandEdited.current = true;
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    assignPlan.mutate(
      {
        planCode,
        status: isFree ? 'ACTIVE' : status,
        until: isFree ? undefined : new Date(dateValue).toISOString(),
        billingCycle: isFree ? undefined : billingCycle,
        note: note.trim() || undefined,
      },
      { onSuccess: () => onOpenChange(false) },
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5 py-4">
      <div className="space-y-2">
        <Label htmlFor="assign-plan-code">Gói</Label>
        <Select value={planCode} onValueChange={(v) => handlePlanChange(v as (typeof PLAN_OPTIONS)[number])}>
          <SelectTrigger id="assign-plan-code">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PLAN_OPTIONS.map((code) => (
              <SelectItem key={code} value={code}>
                {code}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {!isFree && (
        <div className="space-y-2">
          <Label htmlFor="assign-plan-status">Trạng thái</Label>
          <Select value={status} onValueChange={(v) => setStatus(v as typeof status)}>
            <SelectTrigger id="assign-plan-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((s) => (
                <SelectItem key={s.value} value={s.value}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {!isFree && (
        <div className="space-y-2">
          <Label htmlFor="assign-plan-cycle">Chu kỳ</Label>
          <Select value={billingCycle} onValueChange={(v) => handleCycleChange(v as typeof billingCycle)}>
            <SelectTrigger id="assign-plan-cycle">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CYCLE_OPTIONS.map((c) => (
                <SelectItem key={c.value} value={c.value}>
                  {c.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {!isFree && (
        <div className="space-y-2">
          <Label htmlFor="assign-plan-until">Có hiệu lực đến</Label>
          <input
            id="assign-plan-until"
            type="date"
            value={dateValue}
            onChange={handleDateChange}
            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
            required
          />
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="assign-plan-note">Ghi chú (tuỳ chọn)</Label>
        <Textarea
          id="assign-plan-note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Ví dụ: đối tác thiết kế, dùng thử 3 tháng..."
          rows={2}
        />
      </div>

      {isDowngrade && (
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
          {isFreeDowngrade
            ? 'Gói Miễn phí không có đối soát tự động, BankHub sẽ tạm dừng ngay và chỉ mở lại khi nâng gói.'
            : 'Nếu tháng này đã đối soát vượt hạn mức gói mới, tự đối soát sẽ tạm dừng ngay và mở lại đầu tháng sau.'}
        </p>
      )}

      <SheetFooter className="flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-4">
        <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={isPending}>
          Huỷ
        </Button>
        <Button type="submit" disabled={!canSubmit}>
          {isPending ? 'Đang lưu...' : 'Gán gói'}
        </Button>
      </SheetFooter>
    </form>
  );
}
